import React from 'react';
import { Select, ListCollection, Spinner, HStack, Portal } from '@chakra-ui/react';

export type SelectVariant = 'yellow' | 'white';

export interface BaseSelectProps {
    collection: ListCollection<any>;
    value: string | string[];
    onValueChange: (value: string) => void;
    placeholder?: string;
    minW?: string;
    maxW?: any;
    maxH?: any;
    h?: any;
    w?: any;
    disabled?: boolean;
    variant?: SelectVariant;
    isOptionDisabled?: (option: any) => boolean;
    renderOptionLabel?: (option: any) => React.ReactNode;
    isOptionLoading?: boolean;
}

const BaseSelect: React.FC<BaseSelectProps> = ({
    collection,
    value,
    onValueChange,
    placeholder,
    minW,
    maxW,
    maxH,
    disabled = false,
    variant = 'yellow',
    h,
    w,
    isOptionDisabled,
    renderOptionLabel,
    isOptionLoading = false,
}) => {
    const normalizedValue = Array.isArray(value) ? value : value ? [value] : [];

    // Стили для разных вариантов
    const triggerStyles =
        variant === 'yellow'
            ? {
                  borderColor: 'yellow.600',
                  bg: 'yellow.600',
                  color: 'black',
                  _hover: disabled ? undefined : { bg: 'yellow.700' },
              }
            : {
                  borderColor: 'gray.300',
                  bg: 'white',
                  color: 'black',
                  _hover: disabled ? undefined : { bg: 'gray.50' },
              };

    const selectedItemStyles =
        variant === 'yellow'
            ? {
                  bg: 'yellow.600',
                  color: 'black',
                  fontWeight: 400,
                  _hover: { bg: 'yellow.700' },
              }
            : {
                  bg: 'gray.100',
                  color: 'black',
              };

    // Обработчик изменения значения с проверкой на disabled опции
    const handleValueChange = (details: any) => {
        const selectedValue = details.value[0];
        if (selectedValue) {
            // Находим опцию в коллекции
            const selectedOption = collection.items.find((item) => item.value === selectedValue);
            // Проверяем, не отключена ли опция
            if (selectedOption && isOptionDisabled && isOptionDisabled(selectedOption)) {
                // Если опция отключена, не вызываем onValueChange
                return;
            }
            onValueChange(selectedValue);
        }
    };

    return (
        <Select.Root
            collection={collection}
            value={normalizedValue}
            onValueChange={handleValueChange}
            disabled={disabled}
            w={w}
            positioning={{ sameWidth: true, placement: 'bottom-start' }}
        >
            <Select.Trigger
                minW={minW}
                maxW={maxW}
                borderRadius={0}
                h={h || '48px'}
                px={4}
                fontSize="16px"
                cursor={disabled ? 'not-allowed' : 'pointer'}
                border="1px solid"
                {...triggerStyles}
            >
                <Select.ValueText placeholder={placeholder} />
                <Select.Indicator color="black" />
            </Select.Trigger>
            <Portal>
                <Select.Positioner>
                    <Select.Content
                        zIndex={1001}
                        bg="white"
                        border="1px solid"
                        borderColor={variant === 'yellow' ? 'gray.200' : 'gray.300'}
                        borderRadius={0}
                        py={2}
                        maxW={maxW}
                        maxH={maxH}
                    >
                        {isOptionLoading && (
                            <HStack gap={2} align="center" justify="center" w="full" py={2}>
                                <Spinner size="md" color="yellow.600" />
                            </HStack>
                        )}
                        {collection.items.map((option) => {
                            const isDisabled = isOptionDisabled ? isOptionDisabled(option) : false;
                            const label = renderOptionLabel ? renderOptionLabel(option) : option.label;

                            return (
                                <Select.Item
                                    key={option.key ?? option.value}
                                    item={option}
                                    px={4}
                                    py={2}
                                    _disabled={{ opacity: 0.5, cursor: 'not-allowed', bg: 'gray.100' }}
                                    _hover={
                                        isDisabled
                                            ? { bg: 'gray.100', cursor: 'not-allowed' }
                                            : { bg: 'gray.200' }
                                    }
                                    _selected={selectedItemStyles}
                                    opacity={isDisabled ? 0.5 : 1}
                                    bg={isDisabled ? 'gray.100' : 'transparent'}
                                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                                    pointerEvents={isDisabled ? 'none' : 'auto'}
                                >
                                    <Select.ItemText>{label}</Select.ItemText>
                                    {!isDisabled && <Select.ItemIndicator />}
                                </Select.Item>
                            );
                        })}
                    </Select.Content>
                </Select.Positioner>
            </Portal>
        </Select.Root>
    );
};

export default BaseSelect;
